import type React from "react";
import { useState } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { sanitizeBrlValue } from "src/utils/functions";
import type { AmortizationPlan } from "../types";
import { parseBrlToNumber } from "../utils/functions";

interface AmortizationPlanFormProps {
  onAdd: (plan: AmortizationPlan) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

export const AmortizationPlanForm: React.FC<AmortizationPlanFormProps> = ({
  onAdd,
  onError,
}) => {
  const [value, setValue] = useState("");
  const [type, setType] = useState<"prazo" | "parcela">("prazo");
  const [frequency, setFrequency] = useState<"uma_vez" | "multiplas">(
    "uma_vez",
  );
  const [start, setStart] = useState("");
  const [interval, setInterval] = useState("");

  const handleSubmit = () => {
    if (!value || !start) {
      onError("Preencha o valor e a parcela inicial da amortização.");
      return;
    }
    const val = parseBrlToNumber(value);
    const startNum = Number.parseInt(start);
    const intervalNum =
      frequency === "multiplas" ? Number.parseInt(interval) : undefined;

    if (
      isNaN(val) ||
      isNaN(startNum) ||
      (frequency === "multiplas" && isNaN(intervalNum!))
    ) {
      onError("Valores inválidos para amortização.");
      return;
    }

    onAdd({
      id: Math.random().toString(36).substring(2, 11),
      value: val,
      type,
      frequency,
      startInstallment: startNum,
      interval: intervalNum,
    });
  };

  return (
    <div className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Valor da amortização (R$)*</Label>
          <Input
            value={value}
            onChange={(e) => setValue(sanitizeBrlValue(e))}
            placeholder="0,00"
          />
        </div>
        <div className="grid gap-2">
          <Label>Amortizar no prazo ou parcela?</Label>
          <Select
            value={type}
            onValueChange={(v: "prazo" | "parcela") => setType(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="prazo">Prazo</SelectItem>
              <SelectItem value="parcela">Parcela</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Frequência da amortização</Label>
          <Select
            value={frequency}
            onValueChange={(v: "uma_vez" | "multiplas") => setFrequency(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="uma_vez">Uma vez</SelectItem>
              <SelectItem value="multiplas">Múltiplas vezes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Após a parcela:*</Label>
          <Input
            value={start}
            onChange={(e) => setStart(e.target.value.replace(/\D/g, ""))}
            placeholder="Ex: 10"
          />
        </div>
        {frequency === "multiplas" && (
          <div className="grid gap-2">
            <Label>A cada X parcelas:*</Label>
            <Input
              value={interval}
              onChange={(e) =>
                setInterval(e.target.value.replace(/\D/g, ""))
              }
              placeholder="Ex: 12"
            />
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          variant="default"
          type="button"
          className="bg-green-600 hover:bg-green-700 text-white"
          onClick={handleSubmit}
        >
          Adicionar
        </Button>
      </div>
    </div>
  );
};
